import { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { QRCodeSVG } from 'qrcode.react';
import {
  Copy, Clock, CheckCircle2, XCircle, Loader2,
  ArrowRight, AlertTriangle, Coins, Wallet, QrCode
} from 'lucide-react';
import { depositAPI } from '@/lib/api';
import { toast } from 'sonner';

const COIN_COLORS = {
  TRX: '#FF0013', USDT: '#26A17B', BTC: '#F7931A', ETH: '#627EEA',
  BNB: '#F0B90B', SOL: '#9945FF', USDC: '#2775CA', XRP: '#23292F',
  ADA: '#0033AD', DOGE: '#C2A633', AVAX: '#E84142', DOT: '#E6007A',
  MATIC: '#8247E5', LTC: '#BFBBBB', LINK: '#2A5ADA', SHIB: '#FFA409',
};

export function DepositModal({ open, onOpenChange, onSuccess }) {
  const [step, setStep] = useState('select'); // select, deposit, status
  const [coins, setCoins] = useState([]);
  const [selectedCoin, setSelectedCoin] = useState(null);
  const [amount, setAmount] = useState('');
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const pollRef = useRef(null);
  const timerRef = useRef(null);

  // Load supported coins
  useEffect(() => {
    if (open) {
      loadCoins();
      setStep('select');
      setSession(null);
      setSelectedCoin(null);
      setAmount('');
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [open]);

  const loadCoins = async () => {
    try {
      const res = await depositAPI.getSupportedCoins();
      setCoins(res.data.coins || []);
    } catch (err) {
      console.error('Failed to load coins:', err);
    }
  };

  const handleStartDeposit = async () => {
    if (!selectedCoin) return;
    setLoading(true);
    try {
      const res = await depositAPI.createSession({
        coin_symbol: selectedCoin.symbol,
        amount: parseFloat(amount) || 0,
      });
      setSession(res.data);
      setStep('deposit');
      startTimer(res.data.expires_at);
      startPolling(res.data.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create deposit session');
    } finally {
      setLoading(false);
    }
  };

  const startTimer = (expiresAt) => {
    if (timerRef.current) clearInterval(timerRef.current);
    const updateTimer = () => {
      const now = new Date().getTime();
      const expires = new Date(expiresAt).getTime();
      const diff = Math.max(0, Math.floor((expires - now) / 1000));
      setTimeLeft(diff);
      if (diff <= 0) {
        clearInterval(timerRef.current);
        setSession(prev => prev ? { ...prev, status: 'expired' } : null);
      }
    };
    updateTimer();
    timerRef.current = setInterval(updateTimer, 1000);
  };

  const startPolling = (sessionId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await depositAPI.getSession(sessionId);
        setSession(res.data);
        if (res.data.status === 'confirmed') {
          clearInterval(pollRef.current);
          clearInterval(timerRef.current);
          setStep('status');
          if (onSuccess) onSuccess();
        } else if (res.data.status === 'expired' || res.data.status === 'failed') {
          clearInterval(pollRef.current);
          clearInterval(timerRef.current);
          setStep('status');
        }
      } catch (err) {
        console.error('Poll error:', err);
      }
    }, 10000); // Poll every 10s
  };

  const handleConfirmTransfer = async () => {
    if (!session?.id) return;
    setLoading(true);
    try {
      // Notify backend that user claims to have sent the transfer
      const res = await depositAPI.confirmTransfer(session.id);
      setSession(prev => ({ ...prev, status: 'checking' }));
      toast.success('Transfer submitted for verification');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error confirming transfer');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (session?.id) {
      try {
        await depositAPI.cancelSession(session.id);
      } catch (err) { /* ignore */ }
    }
    if (pollRef.current) clearInterval(pollRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
    onOpenChange(false);
  };

  const copyAddress = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Address copied!');
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-lg sm:max-w-xl" data-testid="deposit-modal">
        <DialogHeader>
          <DialogTitle className="font-['Space_Grotesk'] flex items-center gap-2">
            <Wallet className="w-5 h-5 text-[hsl(var(--primary))]" />
            {step === 'select' ? 'Deposit Cryptocurrency' : step === 'deposit' ? 'Awaiting Deposit' : 'Deposit Status'}
          </DialogTitle>
          {step === 'select' && (
            <DialogDescription>Select a coin and enter the amount you want to deposit</DialogDescription>
          )}
        </DialogHeader>

        {/* ===== STEP 1: SELECT COIN ===== */}
        {step === 'select' && (
          <div className="space-y-5" data-testid="deposit-step-select">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Select Coin</Label>
              <Select
                value={selectedCoin?.symbol || ''}
                onValueChange={(val) => {
                  const coin = coins.find(c => c.symbol === val);
                  setSelectedCoin(coin);
                }}
              >
                <SelectTrigger data-testid="deposit-coin-select" className="bg-[hsl(var(--background))] border-[hsl(var(--border))] h-12">
                  <SelectValue placeholder="Choose a cryptocurrency" />
                </SelectTrigger>
                <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-h-[300px]">
                  {coins.map((coin) => (
                    <SelectItem key={coin.symbol} value={coin.symbol}>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white" style={{ backgroundColor: COIN_COLORS[coin.symbol] || '#666' }}>
                          {coin.symbol.slice(0, 2)}
                        </div>
                        <span className="font-medium">{coin.name}</span>
                        <span className="text-[hsl(var(--muted-foreground))] text-xs">({coin.symbol})</span>
                        <Badge variant="outline" className="text-[10px] ml-auto">{coin.network}</Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedCoin && (
              <>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Amount ({selectedCoin.symbol})</Label>
                  <Input
                    data-testid="deposit-amount-input"
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder={`Min: ${selectedCoin.min_deposit} ${selectedCoin.symbol}`}
                    className="bg-[hsl(var(--background))] border-[hsl(var(--border))] h-12 font-mono text-lg"
                  />
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">
                    Minimum deposit: <span className="font-semibold">{selectedCoin.min_deposit} {selectedCoin.symbol}</span>
                    <span className="mx-2">|</span>
                    Network: <span className="font-semibold">{selectedCoin.network}</span>
                  </p>
                </div>

                <div className="p-3 rounded-lg bg-yellow-500/5 border border-yellow-500/20">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-[hsl(var(--muted-foreground))]">
                      Only send <strong>{selectedCoin.symbol}</strong> via <strong>{selectedCoin.network}</strong> network.
                      Sending other assets or using wrong network may result in loss of funds.
                    </p>
                  </div>
                </div>

                <Button
                  data-testid="deposit-start-btn"
                  onClick={handleStartDeposit}
                  disabled={loading}
                  className="w-full h-12 bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/90 text-base font-semibold gap-2"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ArrowRight className="w-5 h-5" />}
                  {loading ? 'Creating session...' : 'Start Deposit'}
                </Button>
              </>
            )}
          </div>
        )}

        {/* ===== STEP 2: QR + ADDRESS + TIMER ===== */}
        {step === 'deposit' && session && (
          <div className="space-y-5" data-testid="deposit-step-qr">
            {/* Timer */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-[hsl(var(--secondary))] border border-[hsl(var(--border))]">
              <div className="flex items-center gap-2">
                <Clock className={`w-5 h-5 ${timeLeft < 300 ? 'text-red-400' : 'text-yellow-500'}`} />
                <span className="text-sm text-[hsl(var(--muted-foreground))]">Time remaining</span>
              </div>
              <span className={`font-mono text-2xl font-bold ${timeLeft < 300 ? 'text-red-400' : 'text-[hsl(var(--foreground))]'}`} data-testid="deposit-timer">
                {formatTime(timeLeft)}
              </span>
            </div>

            {/* Coin Info */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ backgroundColor: COIN_COLORS[session.coin_symbol] || '#666' }}>
                  {session.coin_symbol.slice(0, 2)}
                </div>
                <div>
                  <span className="font-semibold">{session.coin_name}</span>
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">{session.network} Network</p>
                </div>
              </div>
              {session.expected_amount > 0 && (
                <div className="text-right">
                  <span className="text-lg font-bold font-mono">{session.expected_amount}</span>
                  <span className="text-sm text-[hsl(var(--muted-foreground))] ml-1">{session.coin_symbol}</span>
                </div>
              )}
            </div>

            {/* QR Code */}
            <div className="flex justify-center p-6 bg-white rounded-xl">
              <QRCodeSVG
                value={session.deposit_address}
                size={200}
                level="H"
                includeMargin={true}
              />
            </div>

            {/* Address */}
            <div className="space-y-2">
              <Label className="text-xs text-[hsl(var(--muted-foreground))]">Deposit Address</Label>
              <div className="flex items-center gap-2">
                <code data-testid="deposit-address" className="font-mono text-xs bg-[hsl(var(--secondary))] px-4 py-3 rounded-lg flex-1 truncate select-all border border-[hsl(var(--border))]">
                  {session.deposit_address}
                </code>
                <Button
                  size="sm"
                  data-testid="deposit-copy-address"
                  onClick={() => copyAddress(session.deposit_address)}
                  className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] h-10 px-4"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Status */}
            {session.status === 'checking' ? (
              <div className="flex items-center justify-center gap-2 p-4 rounded-lg bg-blue-500/5 border border-blue-500/20">
                <Loader2 className="w-5 h-5 animate-spin text-blue-400" />
                <div className="text-center">
                  <span className="text-sm text-blue-400 font-medium block">Verifying your deposit...</span>
                  <span className="text-xs text-[hsl(var(--muted-foreground))]">This may take a few minutes. Please do not close this window.</span>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center gap-2 p-3 rounded-lg bg-yellow-500/5 border border-yellow-500/20">
                <Loader2 className="w-4 h-4 animate-spin text-yellow-500" />
                <span className="text-sm text-yellow-500 font-medium">Waiting for transaction...</span>
              </div>
            )}

            {/* Instructions */}
            <div className="space-y-2 text-xs text-[hsl(var(--muted-foreground))]">
              <p className="flex items-start gap-2">
                <span className="w-4 h-4 rounded-full bg-[hsl(var(--primary))]/10 flex items-center justify-center text-[10px] font-bold text-[hsl(var(--primary))] flex-shrink-0 mt-0.5">1</span>
                Scan the QR code or copy the address above
              </p>
              <p className="flex items-start gap-2">
                <span className="w-4 h-4 rounded-full bg-[hsl(var(--primary))]/10 flex items-center justify-center text-[10px] font-bold text-[hsl(var(--primary))] flex-shrink-0 mt-0.5">2</span>
                Send <strong>{session.coin_symbol}</strong> via <strong>{session.network}</strong> network only
              </p>
              <p className="flex items-start gap-2">
                <span className="w-4 h-4 rounded-full bg-[hsl(var(--primary))]/10 flex items-center justify-center text-[10px] font-bold text-[hsl(var(--primary))] flex-shrink-0 mt-0.5">3</span>
                Click "Confirm Transfer" once you've completed the payment
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                variant="ghost"
                onClick={handleCancel}
                className="flex-1 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] border border-[hsl(var(--border))]"
              >
                Cancel
              </Button>
              {session.status !== 'checking' && (
                <Button
                  data-testid="deposit-confirm-transfer"
                  onClick={handleConfirmTransfer}
                  disabled={loading}
                  className="flex-1 bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/90 font-semibold gap-2"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  Confirm Transfer
                </Button>
              )}
            </div>
          </div>
        )}

        {/* ===== STEP 3: STATUS ===== */}
        {step === 'status' && session && (
          <div className="space-y-5 text-center py-4" data-testid="deposit-step-status">
            {session.status === 'confirmed' ? (
              <>
                <div className="w-20 h-20 mx-auto rounded-full bg-green-500/10 flex items-center justify-center">
                  <CheckCircle2 className="w-10 h-10 text-green-500" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-green-500 font-['Space_Grotesk']">SUCCESS</h3>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Your deposit has been confirmed and credited</p>
                </div>
                {session.confirmed_amount && (
                  <div className="p-3 rounded-lg bg-green-500/5 border border-green-500/20">
                    <span className="text-lg font-bold font-mono text-green-400">{session.confirmed_amount} {session.coin_symbol}</span>
                  </div>
                )}
                {session.tx_hash && (
                  <p className="text-xs font-mono text-[hsl(var(--muted-foreground))] truncate">TX: {session.tx_hash}</p>
                )}
                <Button onClick={() => onOpenChange(false)} className="w-full bg-green-600 hover:bg-green-700 text-white h-11">
                  Done
                </Button>
              </>
            ) : session.status === 'expired' ? (
              <>
                <div className="w-20 h-20 mx-auto rounded-full bg-yellow-500/10 flex items-center justify-center">
                  <Clock className="w-10 h-10 text-yellow-500" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-yellow-500 font-['Space_Grotesk']">SESSION EXPIRED</h3>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
                    The 30-minute deposit window has expired. If you've already sent funds, don't worry - 
                    they will be detected by our auto-scan system and credited to your account.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1">Close</Button>
                  <Button onClick={() => { setStep('select'); setSession(null); }} className="flex-1 bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]">
                    Try Again
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="w-20 h-20 mx-auto rounded-full bg-red-500/10 flex items-center justify-center">
                  <XCircle className="w-10 h-10 text-red-500" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-red-500 font-['Space_Grotesk']">FAILED</h3>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
                    There was an issue with your deposit. Please contact support if funds were sent.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1">Close</Button>
                  <Button onClick={() => { setStep('select'); setSession(null); }} className="flex-1 bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]">
                    Try Again
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
